import { ProductSlugs } from '@deque/billing-utils';
import {
  Button,
  Checkbox,
  Combobox,
  ComboboxOption,
  Loader,
  TextField
} from '@deque/cauldron-react';
import emailValidator from 'email-validator';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getEnterpriseUserInfo,
  type EnterpriseInfo,
  type UserInfoResponse
} from '~/common/utils/admin-client/get-user-info';
import {
  memberHasAccepted,
  type EnterpriseMembers
} from '~/common/utils/billing-client/client-v2';
import { PRODUCT_ENTITLEMENTS } from '~/common/utils/entitlements';
import { getAccessString } from '~/modules/app/utils/access-utils';
import ContentToast from '~/modules/common/components/ContentToast';
import type { SelectedEnterprise } from '../../pages/internal/admin/types';

export interface EditUserFormData {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  enabled: boolean;
  isEnterpriseAdmin: boolean;
  monitorEntitlements: Array<{ subscriptionId: string; mcpWrite: boolean }>;
}

interface EditUserFormProps {
  preloadCustomerData: SelectedEnterprise;
  token: string;
  enterpriseId: string;
  onSubmit: (data: EditUserFormData) => Promise<void>;
  onCancel: () => void;
}

const EditUserForm: React.FC<EditUserFormProps> = ({
  preloadCustomerData,
  token,
  enterpriseId,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] =
    useState<EnterpriseMembers.AcceptedMember | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [isEnterpriseAdmin, setIsEnterpriseAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfoResponse | null>(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  const [userInfoError, setUserInfoError] = useState<Error | null>(null);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<
    string | null
  >(null);

  // Explicit user overrides keyed by subscriptionId; unset keys fall back to the row snapshot at read time.
  const [mcpWriteEdits, setMcpWriteEdits] = useState<Record<string, boolean>>(
    {}
  );

  const switchableEnterprises = useMemo((): (EnterpriseInfo & {
    id: string;
    name: string;
  })[] => {
    if (!userInfo) {
      return [];
    }
    return userInfo.enterprises.filter(
      (e): e is EnterpriseInfo & { id: string; name: string } =>
        e.id !== null && e.name !== null
    );
  }, [userInfo]);

  const showEnterprisePicker = switchableEnterprises.length >= 2;

  const activeEnterprise = useMemo(() => {
    if (!userInfo) {
      return null;
    }
    if (selectedEnterpriseId) {
      return (
        userInfo.enterprises.find(e => e.id === selectedEnterpriseId) ?? null
      );
    }
    return userInfo.enterprises.find(e => e.id !== null) ?? null;
  }, [userInfo, selectedEnterpriseId]);

  // The form submits to the `enterpriseId` prop (the explored enterprise), so
  // mcp-write controls are scoped to *that* enterprise's Monitor subscriptions,
  // independent of the enterprise-switch picker (which only changes the read-only
  // subscription display).
  const editedEnterprise = useMemo(() => {
    if (!userInfo) {
      return null;
    }
    return userInfo.enterprises.find(e => e.id === enterpriseId) ?? null;
  }, [userInfo, enterpriseId]);

  const monitorRows = useMemo(() => {
    if (!editedEnterprise) {
      return [];
    }
    return editedEnterprise.subscriptionList
      .filter(
        s =>
          s.product_slug === ProductSlugs.axeMonitor &&
          s.entitlements !== undefined
      )
      .map(s => ({
        subscriptionId: s.id,
        accessType: s.access_type ?? '',
        mcpWrite:
          s.entitlements?.[
            PRODUCT_ENTITLEMENTS[ProductSlugs.axeMonitor].mcpWrite
          ] === true
      }));
  }, [editedEnterprise]);

  const acceptedMembers = useMemo(() => {
    return preloadCustomerData.enterpriseMembers
      .filter(memberHasAccepted)
      .sort((a, b) => {
        const nameA =
          [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email;
        const nameB =
          [b.first_name, b.last_name].filter(Boolean).join(' ') || b.email;
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });
  }, [preloadCustomerData.enterpriseMembers]);

  const formatSubscriptionValue = useCallback(
    (sub: { purchase_state: string; start_date: string; end_date: string }) => {
      const start = sub.start_date
        ? new Date(sub.start_date).toLocaleDateString()
        : t('N/A');
      const end = sub.end_date
        ? new Date(sub.end_date).toLocaleDateString()
        : t('N/A');
      return `${sub.purchase_state} | ${start} - ${end}`;
    },
    [t]
  );

  const handleEnterpriseSelect = useCallback((selectedId: string) => {
    setSelectedEnterpriseId(selectedId);
  }, []);

  const handleUserSelect = useCallback(
    async (userId: string) => {
      const member = acceptedMembers.find(m => m.user_id === userId);
      if (!member) {
        return;
      }

      setSelectedUser(member);
      setUserInfo(null);
      setUserInfoError(null);
      setLoadingUserInfo(true);
      setSelectedEnterpriseId(null);
      // Drop the prior user's mcp-write overrides; subscription IDs are shared
      // across members of an enterprise, so an unreset map would leak one user's
      // edits onto the next.
      setMcpWriteEdits({});

      try {
        const info = await getEnterpriseUserInfo({
          token,
          enterpriseId,
          userId
        });
        setUserInfo(info);
        // Pre-fill editable fields from the full info
        setFirstName(info.firstName);
        setLastName(info.lastName);
        setEmail(info.email);
        setEnabled(info.enabled);
        setIsEnterpriseAdmin(info.isEnterpriseAdmin);
        setEmailError(null);

        // Auto-select first enterprise if user has 2+ enterprises
        const switchable = info.enterprises.filter(
          e => e.id !== null && e.name !== null
        );
        if (switchable.length >= 2 && switchable[0].id) {
          setSelectedEnterpriseId(switchable[0].id);
        }
      } catch (err) {
        setUserInfoError(err instanceof Error ? err : new Error(String(err)));
        // Fallback to member data
        setFirstName(member.first_name || '');
        setLastName(member.last_name || '');
        setEmail(member.email);
        setIsEnterpriseAdmin(member.is_admin);
        setEnabled(true);
        setEmailError(null);
      } finally {
        setLoadingUserInfo(false);
      }
    },
    [acceptedMembers, token, enterpriseId]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError(null);

      if (!selectedUser) {
        return;
      }

      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        setEmailError(t('Email is required'));
        return;
      }
      if (!emailValidator.validate(trimmedEmail)) {
        setEmailError(t('Please enter a valid email address'));
        return;
      }

      setSubmitting(true);
      try {
        const monitorEntitlements = monitorRows
          .filter(
            row =>
              (mcpWriteEdits[row.subscriptionId] ?? row.mcpWrite) !==
              row.mcpWrite
          )
          .map(row => ({
            subscriptionId: row.subscriptionId,
            mcpWrite: mcpWriteEdits[row.subscriptionId] ?? row.mcpWrite
          }));

        await onSubmit({
          userId: selectedUser.user_id,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: trimmedEmail.toLowerCase(),
          enabled,
          isEnterpriseAdmin,
          monitorEntitlements
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      selectedUser,
      firstName,
      lastName,
      email,
      enabled,
      isEnterpriseAdmin,
      monitorRows,
      mcpWriteEdits,
      onSubmit,
      t
    ]
  );

  return (
    <form onSubmit={handleSubmit}>
      <Combobox label={t('Select User')}>
        {acceptedMembers.map(member => {
          const name = [member.first_name, member.last_name]
            .filter(Boolean)
            .join(' ');
          const label = name ? `${name} [${member.email}]` : member.email;
          return (
            <ComboboxOption
              key={member.user_id}
              value={label}
              onClick={() => handleUserSelect(member.user_id)}
            >
              {label}
            </ComboboxOption>
          );
        })}
      </Combobox>

      {selectedUser && loadingUserInfo && (
        <Loader label={t('Loading user info...')} />
      )}

      {selectedUser && userInfoError && (
        <ContentToast type="caution" show>
          <strong>{t('Error loading user info -')}</strong>
          <code>{userInfoError.message}</code>
        </ContentToast>
      )}

      {selectedUser && !loadingUserInfo && (
        <>
          {/* Read-only fields (disabled) */}
          {userInfo && (
            <>
              <h3>{t('User Information')}</h3>
              <TextField
                label={t('Username')}
                name="username"
                value={userInfo.username}
                disabled
              />
              <TextField
                label={t('Email Verified')}
                name="emailVerified"
                value={userInfo.emailVerified ? t('Yes') : t('No')}
                disabled
              />
              <TextField
                label={t('Created Date')}
                name="createDate"
                value={userInfo.createDate}
                disabled
              />
              <TextField
                label={t('Payment Type')}
                name="paymentType"
                value={userInfo.paymentType}
                disabled
              />
              <TextField
                label={t('Billing User')}
                name="billingUser"
                value={userInfo.billingUser ? t('Yes') : t('No')}
                disabled
              />
              <TextField
                label={t('Stripe ID')}
                name="stripeId"
                value={userInfo.stripeId || t('N/A')}
                disabled
              />

              {/* Enterprise Picker (shown when 2+ enterprises) */}
              {showEnterprisePicker && (
                <Combobox
                  label={t('Switch Enterprise')}
                  value={
                    switchableEnterprises.find(
                      e => e.id === selectedEnterpriseId
                    )?.name ?? ''
                  }
                >
                  {switchableEnterprises.map(ent => (
                    <ComboboxOption
                      key={ent.id}
                      value={ent.name}
                      onClick={() => handleEnterpriseSelect(ent.id)}
                    >
                      {ent.name}
                    </ComboboxOption>
                  ))}
                </Combobox>
              )}

              {/* Enterprise Subscriptions — shows selected enterprise's products */}
              {activeEnterprise && activeEnterprise.id !== null && (
                <>
                  <h3>{t('Subscriptions')}</h3>
                  {activeEnterprise.name && (
                    <TextField
                      label={t('Enterprise')}
                      name={`enterprise-${activeEnterprise.id}`}
                      value={`${activeEnterprise.name} — ${activeEnterprise.paymentType}`}
                      disabled
                    />
                  )}
                  {activeEnterprise.subscriptionList.map(sub => (
                    <TextField
                      key={sub.id}
                      label={sub.product_slug}
                      name={`subscription-${sub.id}`}
                      value={formatSubscriptionValue(sub)}
                      disabled
                    />
                  ))}
                </>
              )}

              {/* Individual Subscriptions (disabled fields) */}
              {userInfo.subscriptionList &&
                userInfo.subscriptionList.length > 0 && (
                  <>
                    <h3>{t('Individual Subscriptions')}</h3>
                    {userInfo.subscriptionList.map(sub => (
                      <TextField
                        key={sub.id}
                        label={sub.product_slug}
                        name={`individual-sub-${sub.id}`}
                        value={formatSubscriptionValue(sub)}
                        disabled
                      />
                    ))}
                  </>
                )}

              {monitorRows.length > 0 && (
                <>
                  <h3>{t('Monitor MCP Write Access')}</h3>
                  {monitorRows.map(row => {
                    const multiSub = monitorRows.length > 1;
                    const accessLabel = multiSub
                      ? t('Monitor Access Level ({{subscription}})', {
                          subscription: row.subscriptionId
                        })
                      : t('Monitor Access Level');
                    const mcpLabel = multiSub
                      ? t('MCP write access ({{subscription}})', {
                          subscription: row.subscriptionId
                        })
                      : t('MCP write access');
                    return (
                      <div
                        key={row.subscriptionId}
                        role="group"
                        aria-label={
                          multiSub
                            ? t('Monitor subscription {{subscription}}', {
                                subscription: row.subscriptionId
                              })
                            : t('Monitor subscription')
                        }
                      >
                        <TextField
                          label={accessLabel}
                          name={`monitor-access-${row.subscriptionId}`}
                          value={getAccessString(t, row.accessType)}
                          disabled
                        />
                        <Checkbox
                          id={`mcp-write-${row.subscriptionId}`}
                          label={mcpLabel}
                          name={`mcpWrite-${row.subscriptionId}`}
                          checked={
                            mcpWriteEdits[row.subscriptionId] ?? row.mcpWrite
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setMcpWriteEdits(prev => ({
                              ...prev,
                              [row.subscriptionId]: e.target.checked
                            }))
                          }
                        />
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* Editable fields */}
          <h3>{t('Edit User Details')}</h3>

          <TextField
            label={t('First Name')}
            name="firstName"
            value={firstName}
            onChange={(value: string) => setFirstName(value)}
          />
          <TextField
            label={t('Last Name')}
            name="lastName"
            value={lastName}
            onChange={(value: string) => setLastName(value)}
          />
          <TextField
            label={t('Email')}
            name="email"
            type="email"
            required
            value={email}
            onChange={(value: string) => setEmail(value)}
            error={emailError || undefined}
          />
          <Checkbox
            id="edit-user-enabled-checkbox"
            label={t('Enabled')}
            name="enabled"
            checked={enabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEnabled(e.target.checked)
            }
          />
          <Checkbox
            id="edit-user-admin-checkbox"
            label={t('Enterprise Admin')}
            name="isEnterpriseAdmin"
            checked={isEnterpriseAdmin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setIsEnterpriseAdmin(e.target.checked)
            }
          />
          <div>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <Loader label={t('Saving changes...')} />
              ) : (
                t('Save Changes')
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('Cancel')}
            </Button>
          </div>
        </>
      )}
    </form>
  );
};

export default EditUserForm;
